import { useState, useEffect } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp'
import { toast } from 'sonner'
import { Loader2, TrendingUp, Dumbbell, BookOpen, Timer, ArrowLeft, Mail, KeyRound, Check } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Language Selector Component
const LanguageSelector = ({ locale, setLocale, className = '' }) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => setLocale('es-MX', false)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
          locale === 'es-MX'
            ? 'bg-primary text-primary-foreground'
            : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
        }`}
        data-testid="lang-es-btn"
      >
        <span>🇲🇽</span>
        <span>Español</span>
        {locale === 'es-MX' && <Check className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={() => setLocale('en-US', false)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
          locale === 'en-US'
            ? 'bg-primary text-primary-foreground'
            : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
        }`}
        data-testid="lang-en-btn"
      >
        <span>🇺🇸</span>
        <span>English</span>
        {locale === 'en-US' && <Check className="w-3.5 h-3.5" />}
      </button>
    </div>
  )
}

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  })
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetStep, setResetStep] = useState(1) // 1: email, 2: code, 3: new password
  const [resetEmail, setResetEmail] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resetError, setResetError] = useState('')
  
  const { login, register } = useAuth()
  const { t, locale, setLocale } = useLanguage()

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password)
        // Update locale from user data after login
        if (userData.locale) {
          setLocale(userData.locale, false)
        }
        toast.success(t('auth.welcomeBack'))
      } else {
        // Pass locale to registration
        await register(formData.name, formData.email, formData.password, locale)
        toast.success(t('auth.accountCreated'))
      }
    } catch (error) {
      const message = error.response?.data?.detail || t('auth.errorOccurred')
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetError(t('auth.enterEmail'))
      return
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(resetEmail)) {
      setResetError(t('auth.invalidEmail'))
      return
    }
    
    setLoading(true)
    setResetError('')
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail })
      console.log('[OTP] Success:', response.data)
      toast.success(t('auth.codeSent'))
      setResetStep(2)
      setResendCooldown(60) // 60 second cooldown
    } catch (error) {
      console.error('[OTP] Error:', error.response?.data || error.message)
      
      const status = error.response?.status
      const detail = error.response?.data?.detail || ''
      
      // Handle specific HTTP status codes first
      if (status === 404) {
        setResetError(t('auth.emailNotFound'))
      } else if (status === 429) {
        setResetError(t('auth.tooManyAttempts'))
      } else if (status === 500) {
        // Check for specific error types in the detail message
        if (detail.includes('dominio') || detail.includes('verificado')) {
          setResetError(t('auth.emailServiceError'))
        } else if (detail.includes('autenticación') || detail.includes('API key')) {
          setResetError(t('auth.serverConfigError'))
        } else {
          // Show the actual error detail from backend
          setResetError(detail || t('auth.serverError'))
        }
        toast.error(detail || t('auth.serverError'))
      } else if (!error.response) {
        // Network error - no response from server
        setResetError(t('auth.connectionError'))
      } else {
        // Other errors
        setResetError(detail || t('auth.tryAgain'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordsMismatch'))
      return
    }
    
    if (newPassword.length < 6) {
      toast.error(t('auth.passwordMinLength'))
      return
    }
    
    setLoading(true)
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      })
      toast.success(t('auth.passwordUpdated'))
      closeForgotPassword()
    } catch (error) {
      const message = error.response?.data?.detail || t('auth.invalidCode')
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const closeForgotPassword = () => {
    setShowForgotPassword(false)
    setResetStep(1)
    setResetEmail('')
    setResetCode('')
    setNewPassword('')
    setConfirmPassword('')
    setResetError('')
    setResendCooldown(0)
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Branding */}
      <div className="lg:w-1/2 bg-zinc-900 text-white p-8 lg:p-16 flex flex-col justify-center">
        <div className="max-w-md mx-auto lg:mx-0">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-4 tracking-tight">
            Kaizen<span className="text-sky-400">OS</span>
          </h1>
          <p className="text-zinc-400 text-lg mb-12">
            {t('landing.tagline')}
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-zinc-800/50">
              <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-sky-400" />
              </div>
              <span className="text-sm font-medium">{t('landing.habits')}</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-zinc-800/50">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-sm font-medium">{t('landing.finances')}</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-zinc-800/50">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Timer className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-sm font-medium">{t('landing.fasting')}</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-zinc-800/50">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-pink-400" />
              </div>
              <span className="text-sm font-medium">{t('landing.productivity')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="lg:w-1/2 p-8 lg:p-16 flex items-center justify-center bg-background">
        <Card className="w-full max-w-md border-0 shadow-none lg:shadow-xl lg:border">
          <CardHeader className="space-y-1">
            {/* Language selector on registration */}
            {!isLogin && (
              <div className="flex justify-center mb-4">
                <LanguageSelector locale={locale} setLocale={setLocale} />
              </div>
            )}
            <CardTitle className="text-2xl font-bold">
              {isLogin ? t('auth.login') : t('auth.register')}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? t('auth.loginDescription')
                : t('auth.registerDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">{t('auth.name')}</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder={t('auth.namePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="forgot-password-btn"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder={t('auth.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold rounded-full"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLogin ? t('auth.submitLogin') : t('auth.submitRegister')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="auth-toggle-btn"
              >
                {isLogin 
                  ? t('auth.noAccount')
                  : t('auth.hasAccount')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resetStep > 1 && (
                <button
                  onClick={() => setResetStep(prev => prev - 1)}
                  className="p-1 rounded-full hover:bg-secondary transition-colors"
                  data-testid="reset-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              {resetStep === 1 && t('auth.recoverPassword')}
              {resetStep === 2 && t('auth.verifyCode')}
              {resetStep === 3 && t('auth.newPassword')}
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 && t('auth.enterEmailForCode')}
              {resetStep === 2 && t('auth.enterCode')}
              {resetStep === 3 && t('auth.createNewPassword')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Step 1: Email */}
            {resetStep === 1 && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-sky-500/10 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-sky-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.email')}</Label>
                  <Input
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value)
                      setResetError('')
                    }}
                    data-testid="reset-email-input"
                  />
                  {resetError && (
                    <p className="text-sm text-destructive flex items-center gap-1" data-testid="reset-error">
                      <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                      {resetError}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleForgotPassword}
                  disabled={loading || !resetEmail}
                  className="w-full rounded-full"
                  data-testid="send-code-btn"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.sendCode')}
                </Button>
              </>
            )}

            {/* Step 2: Code verification */}
            {resetStep === 2 && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center">
                    <KeyRound className="w-8 h-8 text-violet-500" />
                  </div>
                </div>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={resetCode}
                    onChange={(value) => setResetCode(value)}
                    data-testid="reset-code-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  {t('auth.codeSentTo')} <span className="font-medium">{resetEmail}</span>
                </p>
                <Button
                  onClick={() => setResetStep(3)}
                  disabled={resetCode.length !== 6}
                  className="w-full rounded-full"
                  data-testid="verify-code-btn"
                >
                  {t('auth.verifyCodeBtn')}
                </Button>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || resendCooldown > 0}
                >
                  {loading 
                    ? t('auth.sending')
                    : resendCooldown > 0 
                      ? t('auth.resendCodeIn', { seconds: resendCooldown })
                      : t('auth.resendCode')
                  }
                </button>
              </>
            )}

            {/* Step 3: New password */}
            {resetStep === 3 && (
              <>
                <div className="space-y-2">
                  <Label>{t('auth.newPassword')}</Label>
                  <Input
                    type="password"
                    placeholder={t('auth.passwordPlaceholder')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.confirmPassword')}</Label>
                  <Input
                    type="password"
                    placeholder={t('auth.passwordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  onClick={handleResetPassword}
                  disabled={loading || !newPassword || !confirmPassword}
                  className="w-full rounded-full"
                  data-testid="reset-password-btn"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.changePassword')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
